import { useEffect, useState } from 'react';
import { sportGroups, type SportKey } from '../lib/sport-groups';
import { getMyProfile, type MyProfileData } from '../lib/api';
import { axisName, fmtNum, getLang, localeOf, useT } from '../lib/i18n';

/**
 * The top of the player's My profile: their level, their skills and their
 * last four weeks. The journal follows underneath.
 *
 * Built from design B. Everything shown comes from /api/me/profile, which
 * sends only what the player may see.
 */

type BandKey = 'co.rate.w1' | 'co.rate.w3' | 'co.rate.w5' | 'co.rate.w7' | 'co.rate.w9';

/** The 0–10 band an average sits in, rounded the way a coach would read it: 4.8 is a 5. */
function band(score: number): BandKey {
  const r = Math.round(score);
  return r <= 2 ? 'co.rate.w1' : r <= 4 ? 'co.rate.w3' : r <= 6 ? 'co.rate.w5' : r <= 8 ? 'co.rate.w7' : 'co.rate.w9';
}

const dateFmt = (iso: string) =>
  new Intl.DateTimeFormat(localeOf(getLang()), { day: 'numeric', month: 'long' }).format(new Date(iso));

export function MyProfile() {
  const [data, setData] = useState<MyProfileData | null | false>(null);
  useEffect(() => { getMyProfile().then(setData).catch(() => setData(false)); }, []);
  if (data === null) return <div className="list"><div className="skeleton" style={{ height: 120 }} /></div>;
  if (data === false) return null;
  return (
    <div className="mp">
      {/* The level is on padel's scale: not for a tennis player. */}
      {data.level && data.sport?.client !== 'tennis' && <LevelCard level={data.level} />}
      {/* By sport when the coach teaches both (lib/sport-groups.ts); as before otherwise. */}
      {data.skills.length === 0 ? <Skills skills={[]} />
        : sportGroups(data.skills, data.sport?.coach).map((g) => <Skills key={g.sport ?? 'all'} skills={g.items} sport={g.sport} />)}
      <FourWeeks weeks={data.weeks} />
    </div>
  );
}

function LevelCard({ level }: { level: NonNullable<MyProfileData['level']> }) {
  const t = useT();
  return (
    <div className="mp-level">
      <span className="mp-level__num">{fmtNum(level.value, 1)}</span>
      <span className="mp-level__text">
        <span className="mp-level__label">{t('mp.level')}</span>
        <span className="mp-level__by">
          {level.setBy ? t('mp.levelSetBy', { coach: level.setBy, date: dateFmt(level.setAt) }) : dateFmt(level.setAt)}
        </span>
      </span>
    </div>
  );
}

function Skills({ skills, sport }: { skills: MyProfileData['skills']; sport?: SportKey | null }) {
  const t = useT();
  // «Навыки · Теннис» when the coach teaches both sports.
  const title = sport ? `${t('mp.skills')} · ${t(sport === 'padel' ? 'co.sport.padel' : 'co.sport.tennis')}` : t('mp.skills');
  if (skills.length === 0) {
    return (
      <>
        <div className="mp-title">{t('mp.skills')}</div>
        <div className="mp-card mp-empty">{t('mp.noRatings')}</div>
      </>
    );
  }
  const named = skills.map((s) => ({ ...s, label: axisName(s) }));
  return (
    <>
      <div className="mp-title">{title}</div>
      <div className="mp-card mp-map">
        <SkillMap skills={named} />
        <span className="mp-legend"><i aria-hidden="true" />{t('mp.training')}</span>
      </div>
      <div className="mp-card mp-rows">
        {named.map((s) => {
          // Last 30 days against the 30 before; null unless both had ratings.
          const delta = s.change;
          const shown = delta !== null && Math.abs(delta) >= 0.05;
          return (
            <div className="mp-row" key={s.axisId}>
              <span className="mp-row__who">
                <span className="mp-row__name">{s.label}</span>
                <span className="mp-row__band">{t(band(s.score))}</span>
              </span>
              <Spark values={s.trend} />
              <span className="mp-row__score">{fmtNum(s.score, 1)}</span>
              <span className={`mp-row__delta${shown ? (delta! > 0 ? ' mp-up' : ' mp-down') : ''}`}>
                {shown ? `${delta! > 0 ? '+' : '−'}${fmtNum(Math.abs(delta!), 1)}` : ''}
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
}

/** One shape: training. Matches are not drawn for now. */
function SkillMap({ skills }: { skills: { axisId: string; label: string; score: number }[] }) {
  const t = useT();
  const W = 326, H = 236, cx = W / 2, cy = 116, R = 80;
  const n = skills.length;
  const at = (i: number, v: number) => {
    const a = (Math.PI * 2 * i) / n - Math.PI / 2;
    const r = (Math.max(0, Math.min(10, v)) / 10) * R;
    return [cx + Math.cos(a) * r, cy + Math.sin(a) * r];
  };
  const ring = (v: number) => skills.map((_, i) => at(i, v).map((x) => x.toFixed(1)).join(',')).join(' ');
  const shape = skills.map((s, i) => at(i, s.score).map((x) => x.toFixed(1)).join(',')).join(' ');
  return (
    <svg className="mp-radar" viewBox={`0 0 ${W} ${H}`} role="img"
      aria-label={`${t('mp.skills')}: ${skills.map((s) => `${s.label} ${fmtNum(s.score, 1)}`).join(', ')}`}>
      {[10, 7.5, 5, 2.5].map((v) => <polygon key={v} points={ring(v)} className="mp-radar__ring" />)}
      {skills.map((s, i) => {
        const [x, y] = at(i, 10);
        return <line key={s.axisId} x1={cx} y1={cy} x2={x} y2={y} className="mp-radar__ring" />;
      })}
      <polygon points={shape} className="mp-radar__shape" />
      {skills.map((s, i) => {
        const [x, y] = at(i, 12.4);
        const anchor = Math.abs(x - cx) < 4 ? 'middle' : x > cx ? 'start' : 'end';
        return <text key={s.axisId} x={x} y={y} textAnchor={anchor} dominantBaseline="middle" className="mp-radar__label">{s.label}</text>;
      })}
    </svg>
  );
}

/** The recent ratings for one skill, oldest first, as a small line. */
function Spark({ values }: { values: number[] }) {
  if (values.length < 2) return <span className="mp-spark" aria-hidden="true" />;
  const W = 52, H = 18;
  const pts = values.map((v, i) => `${((i / (values.length - 1)) * (W - 4) + 2).toFixed(1)},${(H - 2 - ((v - 1) / 9) * (H - 4)).toFixed(1)}`);
  return (
    <svg className="mp-spark" viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      <polyline points={pts.join(' ')} />
    </svg>
  );
}

function FourWeeks({ weeks }: { weeks: MyProfileData['weeks'] }) {
  const t = useT();
  const byDay = new Map(weeks.days.map((d) => [d.day, d]));
  const start = new Date(`${weeks.from}T12:00:00Z`);
  const cells = Array.from({ length: 28 }, (_, i) => {
    const d = new Date(start.getTime() + i * 86400000);
    const key = d.toISOString().slice(0, 10);
    return { key, day: d.getUTCDate(), info: byDay.get(key), future: key > weeks.today, today: key === weeks.today };
  });
  const heads = Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(localeOf(getLang()), { weekday: 'short', timeZone: 'UTC' })
      .format(new Date(start.getTime() + i * 86400000)));
  const lessons = weeks.days.reduce((a, d) => a + d.lessons, 0);
  const missed = weeks.days.reduce((a, d) => a + d.noShows, 0);

  return (
    <>
      <div className="mp-title">{t('mp.fourWeeks')}</div>
      <div className="mp-card mp-weeks">
        <div className="mp-grid">
          {heads.map((h, i) => <span key={i} className="mp-grid__head">{h}</span>)}
          {cells.map((c) => {
            const state = c.future ? 'future' : c.info?.lessons ? 'lesson' : c.info?.noShows ? 'noshow' : 'none';
            const label = state === 'lesson' ? t('mp.dayLesson') : state === 'noshow' ? t('mp.dayNoShow') : '';
            return (
              <span key={c.key} className={`mp-day mp-day--${state}${c.today ? ' mp-day--today' : ''}`}
                title={label || undefined} aria-label={label ? `${c.day}: ${label}` : undefined}>
                {c.day}
              </span>
            );
          })}
        </div>
        <div className="mp-weeks__foot">
          <span>{t('mp.sessionsCount', { n: lessons, sessions: t.word('co.today.sessions', lessons) })}</span>
          {missed > 0 && <span>{t('mp.noShowsCount', { n: missed })}</span>}
        </div>
        <div className="mp-weeks__key">
          <span><i className="mp-day mp-day--lesson" aria-hidden="true" />{t('mp.dayLesson')}</span>
          <span><i className="mp-day mp-day--noshow" aria-hidden="true" />{t('mp.dayNoShow')}</span>
        </div>
      </div>
    </>
  );
}
